// Third Party Imports
import React from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import PropTypes from 'prop-types';
import {get} from 'lodash';

// Relative Imports
import appActions from '../../actions/actions';
import userInfoActions from '../../actions/userInfo';
import vatInformationActions from '../../actions/vatInformation';
import businessModelActions from '../../actions/businessModel';
import Summary from './Summary';
import {isAuSite, isNzSite, isUsSite} from '../../utils/validate';
import {
    isQstEnabled, isQstEligible, isAddressAvailable, getAddressFromUserInfoByType
} from '../../utils/commonUtil';
import {BILLING, CONTACT} from '../../utils/constants';

function mapStateToProps(state) {
    return {
        translations: state.app.translations,
        publicUuid: get(state, 'app.publicUuid', ''),
        firstName: get(state, 'userInfoDetails.userInfoMetadata.user.firstName', ''),
        lastName: get(state, 'userInfoDetails.userInfoMetadata.user.lastName', ''),
        emailAddress: get(state, 'userInfoDetails.userInfoMetadata.user.emailAddress', ''),
        companyName: get(state, 'userInfoDetails.userInfoMetadata.user.companyName', ''),
        softwareName: get(state, 'userInfoDetails.userInfoMetadata.ipm.resellerName', ''),
        softwareUserId: get(state, 'userInfoDetails.userInfoMetadata.ipm.externalId', ''),
        companyCountryCode: get(state, 'userInfoDetails.userInfoMetadata.companyCountryCode', ''),
        propertiesManaged: get(state, 'userInfoDetails.userInfoMetadata.ipm.propertyCount', 0),
        companyRegistration: get(state, 'userInfoDetails.userInfoMetadata.ipm.companyRegistration', ''),
        softwareDetails: get(state, 'softwareDetails', ''),
        serviceError: get(state, 'userInfoDetails.errorKey', ''),
        isSaveModalOpen: get(state, 'app.isSaveModalOpen', false),
        hostName: state.system.hostName,
        siteName: state.system.siteName,
        userInfoMetadata: get(state, 'userInfoDetails.userInfoMetadata', {}),
        dyff: get(state, 'app.dyff', {}),
        advertiserBusinessModel: get(state, 'businessModel.advertiserBusinessModel', null)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        appActions: bindActionCreators(appActions, dispatch),
        userInfoActions: bindActionCreators(userInfoActions, dispatch),
        vatInformationActions: bindActionCreators(vatInformationActions, dispatch),
        businessModelActions: bindActionCreators(businessModelActions, dispatch),
        dispatch
    };
}

@connect(mapStateToProps, mapDispatchToProps)
class SummaryContainer extends React.Component {
    constructor(props) {
        super(props);
        this.triggerEdapPageViewAction = this.triggerEdapPageViewAction.bind(this);
        this.handleContinueButtonClick = this.handleContinueButtonClick.bind(this);
        this.handleAlertDismiss = this.handleAlertDismiss.bind(this);
        this.handleSaveAndFinishOnClick = this.handleSaveAndFinishOnClick.bind(this);
        this.handleSaveModalClose = this.handleSaveModalClose.bind(this);
    }

    componentWillMount() {
        this.props.appActions.enableLoadOverLay(true);
        this.props.userInfoActions.getUserInfo();
        
        // Don't fetch business model here - wait for user info to load first
    }

    componentDidUpdate(prevProps) {
        // Only fetch business model if the dyff flag is enabled
        if (!this.props.dyff?.SHARED_IPM_BANKING_ENABLED) {
            return;
        }
        
        // Fetch business model once user info is loaded and we have the required data
        const hasUserInfo = this.props.softwareName || this.props.softwareUserId;
        const prevHadUserInfo = prevProps.softwareName || prevProps.softwareUserId;
        
        // If we now have user info but didn't before, always fetch business model (even if cached)
        if (hasUserInfo && !prevHadUserInfo) {
            this.props.businessModelActions.getAdvertiserBusinessModel();
        }
    }

    getSoftwareName() {
        return this.props.softwareName ? this.props.softwareName : this.props.softwareDetails.softwareName;
    }

    getSoftwareUserId() {
        return this.props.softwareUserId ? this.props.softwareUserId : this.props.softwareDetails.softwareId;
    }

    handleContinueButtonClick(event) {
        event.preventDefault();
        this.props.appActions.triggerEdapEvent('generic.click', {
            eventcategory: 'Continue'
        }, {});
        this.props.appActions.enableLoadOverLay(true);
        const {companyRegistration, siteName} = this.props;

        if (isUsSite(siteName) || isAuSite(siteName) || isNzSite(siteName)) {
            if (isQstEnabled() && isAddressAvailable(this.props.userInfoMetadata) &&
                isQstEligible(this.props.userInfoMetadata.user.contact.addresses)) {
                this.props.appActions.changeAppState('VAT_INFORMATION');
            } else {
                this.props.appActions.changeAppState('LISTING_MODEL');
            }
        } else if (companyRegistration) {
            const vatDetails = {
                countryCode: this.props.companyCountryCode,
                registrationNumber: companyRegistration
            };
            this.props.vatInformationActions.updateVatDetails(vatDetails);
        } else {
            this.props.appActions.changeAppState('VAT_INFORMATION');
        }
    }

    triggerEdapPageViewAction() {
        this.props.appActions.triggerEdapPageView(this.props.publicUuid, 'Summary Page');
    }

    handleAlertDismiss() {
        this.props.userInfoActions.alertError('');
    }

    handleSaveAndFinishOnClick() {
        this.props.appActions.showSaveAndFinishModal(true);
    }

    handleSaveModalClose() {
        this.props.appActions.showSaveAndFinishModal(false);
    }

    buildAddress(address) {
        return ({
            address1: address.address1 || '',
            address2: address.address2 || '',
            city: address.city || '',
            state: address.stateProvince || '',
            country: address.country || '',
            postalCode: address.postalCode || ''
        });
    }

    render() {
        let shippingAddress = {};
        let billingAddress = {};
        let {userInfoMetadata} = this.props;
        if (isAddressAvailable(userInfoMetadata)) {
            shippingAddress = this.buildAddress(getAddressFromUserInfoByType(userInfoMetadata, CONTACT)[0]);
            billingAddress = this.buildAddress(getAddressFromUserInfoByType(userInfoMetadata, BILLING)[0]);
        }
        return (
            <Summary
                translations={this.props.translations}
                firstName={this.props.firstName}
                lastName={this.props.lastName}
                emailAddress={this.props.emailAddress}
                companyName={this.props.companyName}
                softwareName={this.getSoftwareName()}
                softwareUserId={this.getSoftwareUserId()}
                propertiesManaged={this.props.propertiesManaged}
                address1={shippingAddress.address1}
                address2={shippingAddress.address2}
                city={shippingAddress.city}
                state={shippingAddress.state}
                country={shippingAddress.country}
                postalCode={shippingAddress.postalCode}
                billingAddress1={billingAddress.address1}
                billingAddress2={billingAddress.address2}
                billingCity={billingAddress.city}
                billingState={billingAddress.state}
                billingCountry={billingAddress.country}
                billingPostalCode={billingAddress.postalCode}
                advertiserBusinessModel={this.props.advertiserBusinessModel}
                handleContinueButtonClick={this.handleContinueButtonClick}
                triggerEdapPageViewAction={this.triggerEdapPageViewAction}
                serviceError={this.props.serviceError}
                handleAlertDismiss={this.handleAlertDismiss}
                handleSaveAndFinishOnClick={this.handleSaveAndFinishOnClick}
                isSaveModalOpen={this.props.isSaveModalOpen}
                handleSaveModalClose={this.handleSaveModalClose}
                hostName={this.props.hostName}
                siteName={this.props.siteName}
                sharedIpmBankingEnabled={this.props.dyff?.SHARED_IPM_BANKING_ENABLED}
            />
        );
    }
}

SummaryContainer.propTypes = {
    translations: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    publicUuid: PropTypes.string,
    serviceError: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    emailAddress: PropTypes.string,
    companyName: PropTypes.string,
    softwareName: PropTypes.string,
    softwareUserId: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    billingAddress1: PropTypes.string,
    billingAddress2: PropTypes.string,
    billingCity: PropTypes.string,
    billingCountry: PropTypes.string,
    billingState: PropTypes.string,
    billingPostalCode: PropTypes.string,
    propertiesManaged: PropTypes.number,
    companyCountryCode: PropTypes.string,
    isSaveModalOpen: PropTypes.bool,
    hostName: PropTypes.string,
    siteName: PropTypes.string,
    sharedIpmBankingEnabled: PropTypes.bool,
    userInfoMetadata: PropTypes.any // eslint-disable-line react/forbid-prop-types
};

SummaryContainer.defaultProps = {
    publicUuid: '-1',
    serviceError: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    companyName: '',
    softwareName: '',
    softwareUserId: '',
    address1: '',
    address2: '',
    city: '',
    country: '',
    state: '',
    postalCode: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingCountry: '',
    billingState: '',
    billingPostalCode: '',
    propertiesManaged: 0,
    companyCountryCode: '',
    isSaveModalOpen: false,
    hostName: '',
    siteName: '',
    sharedIpmBankingEnabled: false
};

export default SummaryContainer;
